import React from "react";
import { ArrowDownRight, MapPin } from "lucide-react";
import { Logo } from "../brand/Logo";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Top meta strip */}
        <div className="flex items-end justify-between mb-10 md:mb-16">
          <div className="overline text-inkmute" data-testid="hero-overline">
            Est. 2025 · Curated in the UAE
          </div>
          <div className="hidden md:flex items-center gap-2 overline text-inkmute" data-testid="hero-location">
            <MapPin className="w-3.5 h-3.5" strokeWidth={2.5} />
            <span>Dubai · United Arab Emirates</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          {/* Left: Wordmark + copy */}
          <div className="lg:col-span-8 animate-fade-up">
            <Logo variant="display" />
            <p className="mt-10 max-w-xl text-lg md:text-xl text-ink/80 leading-relaxed font-body">
              Niche, considered nutrition for people who actually
              <em className="font-wordmark text-terracotta"> move</em>. A rice
              alternative that doesn’t compromise. And a pantry of goods you
              simply can’t find here — yet.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-5">
              <a
                href="#products"
                data-testid="hero-shop-button"
                className="group inline-flex items-center gap-3 bg-ink text-bone hover:bg-terracotta transition-colors duration-300 px-7 py-4 rounded-sm font-display font-semibold text-sm tracking-wide"
              >
                Explore the Range
                <ArrowDownRight className="w-4 h-4 group-hover:translate-x-1 group-hover:translate-y-1 transition-transform duration-300" />
              </a>
              <a
                href="#why-us"
                data-testid="hero-why-link"
                className="overline text-ink hover:text-terracotta link-underline"
              >
                Why Thrive by Madsen?
              </a>
            </div>
          </div>

          {/* Right: hero editorial image + Phase 01 credential card */}
          <div className="lg:col-span-4 flex flex-col gap-6 lg:items-end">
            <div
              className="relative w-full max-w-md aspect-[5/4] rounded-sm overflow-hidden bg-surfacealt"
              data-testid="hero-image-panel"
            >
              <img
                src="https://customer-assets-agu9un31.emergentagent.net/job_lifestyle-essentials-3/artifacts/r00hn7iv_ChatGPT%20Image%20Aug%204%2C%202026%2C%2008_13_49%20PM.png"
                alt="Editorial still-life — bowl of shirataki rice with dried fruit and a botanical vase against a soft mountain backdrop"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>

            <div
              className="flex flex-col bg-surface border border-hairline px-6 py-5 rounded-sm max-w-md w-full"
              data-testid="hero-spec-card"
            >
              <span className="overline text-terracotta">Phase 01</span>

              <div className="mt-3">
                <span className="font-display font-bold text-ink text-base leading-tight block">
                  REVIVE — Daily Electrolytes
                </span>
                <span className="text-xs text-inkmute mt-1 font-body block">
                  30 sachet box · 9 flavours
                </span>
              </div>

              <div className="mt-3 pt-3 border-t border-hairline">
                <span className="font-display font-bold text-ink text-base leading-tight block">
                  Oryvita — Dry Shirataki Rice
                </span>
                <span className="text-xs text-inkmute mt-1 font-body block">
                  1 kg · smart rice, zero filler
                </span>
              </div>

              <div className="mt-4 pt-4 border-t border-hairline">
                <span className="overline text-inkmute block mb-1">Next up</span>
                <span className="text-sm font-display font-semibold text-ink leading-tight">
                  Dried fruit · Nut butters · Rare grains
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="mt-20 md:mt-28 border-y border-hairline overflow-hidden py-5">
        <div className="marquee-track animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6 shrink-0">
              {[
                "Oryvita · 41 kcal / serving",
                "Konjac + rice flour · 1kg",
                "Plant-based · Gluten-free · Sugar-free",
                "Sourced, not invented",
                "Curated for the UAE",
                "Dried fruit · coming",
              ].map((item, idx) => (
                <React.Fragment key={idx}>
                  <span className="overline text-ink">{item}</span>
                  <span className="text-terracotta">✦</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
